import { getDirectoryClient } from "../client";

const LANGUAGE_CACHE_KEY = "preferredLanguage";

// Bidirectional language mappings
const LANGUAGE_MAPPINGS = {
  // Profile standard → Assessment format
  profile: {
    c: "c",
    "c++": "cpp",
    "c#": "csharp",
    clojure: "clojure",
    cobol: "cobol",
    elixir: "elixir",
    go: "go",
    java: "java",
    javascript: "javascript",
    kotlin: "kotlin",
    perl: "perl",
    php: "php",
    python: "python",
    ruby: "ruby",
    rust: "rust",
    scala: "scala",
    typescript: "typescript",
  } as { [k: string]: string },
  // Assessment format → Profile standard
  assessment: {
    c: "c",
    cobol: "cobol",
    cpp: "c++",
    csharp: "c#",
    clojure: "clojure",
    elixir: "elixir",
    go: "go",
    java: "java",
    javascript: "javascript",
    kotlin: "kotlin",
    perl: "perl",
    php: "php",
    python: "python",
    pseudocode: "pseudocode",
    ruby: "ruby",
    rust: "rust",
    scala: "scala",
    typescript: "typescript",
  } as { [k: string]: string },
};

/**
 * Always normalize to profile standard (for localStorage consistency)
 * @param {string} language - Language in any format
 * @returns {string|null} Language in profile standard format
 */
const normalizeToProfileStandard = (language: string): string | null => {
  if (!language) return null;
  const lower = language.toLowerCase();

  // If it's already profile standard, return it
  if (LANGUAGE_MAPPINGS.profile[lower]) return lower;

  // If it's assessment format, convert to profile
  if (LANGUAGE_MAPPINGS.assessment[lower]) return LANGUAGE_MAPPINGS.assessment[lower];

  // Fallback to lowercase
  return lower;
};

/**
 * Convert profile standard to assessment format
 * @param {string} profileLanguage - Language in profile standard format
 * @param {string[]} availableLanguages - Available assessment languages
 * @returns {string|null} Language in assessment format if available, null otherwise
 */
const profileToAssessment = (profileLanguage: string, availableLanguages: string[]) => {
  if (!profileLanguage || !availableLanguages?.length) return null;

  const assessmentLanguage = LANGUAGE_MAPPINGS.profile[profileLanguage.toLowerCase()];
  return availableLanguages.includes(assessmentLanguage) ? assessmentLanguage : null;
};

/**
 * Gets the preferred coding language from localStorage, with API fallback
 * @param {string[]} availableLanguages - Optional: Available languages for assessment context
 * @returns {Promise<string|null>} The preferred language or null if not set/available
 */
export const getUserLanguagePreference = async (availableLanguages: string[] | null = null) => {
  // Check localStorage first
  const cached = localStorage.getItem(LANGUAGE_CACHE_KEY);
  let language = cached;

  // Fallback to API if localStorage is empty
  if (!language) {
    try {
      const directoryClient = getDirectoryClient();
      const userData = await directoryClient.whoAmI();
      language = userData?.data?.developerProfile?.language || null;

      // Cache the result (always in profile standard format)
      if (language) {
        const normalizedLanguage = normalizeToProfileStandard(language);
        if (normalizedLanguage) {
          localStorage.setItem(LANGUAGE_CACHE_KEY, normalizedLanguage);
          language = normalizedLanguage;
        }
      }
    } catch (error) {
      console.warn("Failed to fetch user language preference:", error);
      return null;
    }
  }

  // If requesting for assessment context (like KA modal), convert to assessment format
  if (availableLanguages && language) {
    return profileToAssessment(language, availableLanguages);
  }

  // Return profile standard format
  return language;
};

/**
 * Sets the user's preferred coding language in localStorage
 * Always normalizes to profile standard for consistency
 * @param {string} language - The coding language to set as preferred
 */
export const setUserLanguagePreference = (language: string) => {
  // Always normalize to profile standard for localStorage consistency
  const profileLanguage = normalizeToProfileStandard(language);
  if (!profileLanguage) return;
  localStorage.setItem(LANGUAGE_CACHE_KEY, profileLanguage);
};

/**
 * Clears the language preference from localStorage
 */
export const clearLanguagePreferenceCache = () => {
  localStorage.removeItem(LANGUAGE_CACHE_KEY);
};
